import { createRouter, createWebHistory } from 'vue-router'
import { watch } from 'vue'
import { useAuthStore } from '../stores/auth'
import type { UserRole } from '../types'
import { FLEET_ROLES } from '../types'

// Build-time flag (default OFF → disabled in production unless the env sets it).
const FUEL_DASHBOARD_ENABLED = import.meta.env.VITE_FEATURE_FUEL_DASHBOARD === 'true'

declare module 'vue-router' {
  interface RouteMeta {
    guest?: boolean
    requiresAuth?: boolean
    roles?: UserRole[]
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      redirect: '/login'
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/auth/LoginView.vue'),
      meta: { guest: true }
    },
    {
      path: '/invite',
      name: 'invite',
      component: () => import('../views/auth/AcceptInviteView.vue'),
      meta: { guest: true }
    },

    // Legal pages — no auth gates either way; reachable signed in or out.
    {
      path: '/terms',
      name: 'terms',
      component: () => import('../views/legal/TermsOfUse.vue'),
    },
    {
      path: '/privacy',
      name: 'privacy',
      component: () => import('../views/legal/PrivacyPolicy.vue'),
    },

    // Admin / Dispatcher routes
    {
      path: '/admin',
      name: 'admin-dashboard',
      component: () => import('../views/admin/AdminDashboard.vue'),
      meta: { requiresAuth: true, roles: ['super_admin', 'admin', 'dispatcher'] }
    },
    {
      path: '/admin/orders/new',
      name: 'admin-create-order',
      component: () => import('../views/admin/AdminCreateOrder.vue'),
      meta: { requiresAuth: true, roles: ['super_admin', 'admin', 'dispatcher'] }
    },
    {
      path: '/admin/orders/:id',
      name: 'admin-order-detail',
      component: () => import('../views/admin/AdminOrderDetail.vue'),
      meta: { requiresAuth: true, roles: ['super_admin', 'admin', 'dispatcher'] }
    },
    {
      // Both boards for one day, for the dispatcher who works them together (HOP-168).
      // Additive: /admin and /admin/long-range are untouched, because the week-long board
      // is still the better tool for planning.
      path: '/admin/dispatch',
      name: 'dispatch-board',
      component: () => import('../views/admin/DispatchBoard.vue'),
      meta: { requiresAuth: true, roles: ['super_admin', 'admin', 'dispatcher'] }
    },
    {
      path: '/admin/long-range',
      name: 'admin-long-range',
      component: () => import('../views/admin/AdminLongRange.vue'),
      meta: { requiresAuth: true, roles: ['super_admin', 'admin', 'dispatcher'] }
    },
    {
      path: '/admin/fleet',
      component: () => import('../views/admin/AdminFleet.vue'),
      // meta is merged onto child routes (vue-router), so the auth+role gate
      // covers /admin/fleet/vehicles, /trailers and /invoices too.
      //
      // The Invoices tab is inside that gate deliberately, so the maintenance
      // role reaches it too (HOP-130 + HOP-114). The import only ever writes
      // 'repair' and 'parts' expenses, which is exactly what the maintenance
      // RLS policy admits — so the shop can drop an invoice, not merely look
      // at the tab.
      meta: { requiresAuth: true, roles: FLEET_ROLES },
      children: [
        { path: '', redirect: '/admin/fleet/vehicles' },
        {
          path: 'vehicles',
          name: 'admin-fleet-vehicles',
          component: () => import('../views/admin/AdminVehicles.vue'),
        },
        {
          path: 'trailers',
          name: 'admin-fleet-trailers',
          component: () => import('../views/admin/AdminTrailers.vue'),
        },
        {
          // A shop invoice covers many units at once, so it belongs to the fleet
          // rather than to any one unit — but not on top of the registry either.
          path: 'invoices',
          name: 'admin-fleet-invoices',
          component: () => import('../views/admin/AdminFleetInvoices.vue'),
        },
        {
          // Parts in the shop's bin, and the low-stock report.
          path: 'stock',
          name: 'admin-fleet-stock',
          component: () => import('../views/admin/ShopStock.vue'),
        },
      ],
    },
    // Detail pages sit OUTSIDE the /admin/fleet children so they render full-width
    // rather than inside the list tabs. fleetKind drives which asset the page edits.
    {
      path: '/admin/fleet/vehicles/:id',
      name: 'admin-fleet-vehicle-detail',
      component: () => import('../views/admin/AdminFleetItemDetail.vue'),
      meta: { requiresAuth: true, roles: FLEET_ROLES, fleetKind: 'vehicle' },
    },
    {
      path: '/admin/fleet/trailers/:id',
      name: 'admin-fleet-trailer-detail',
      component: () => import('../views/admin/AdminFleetItemDetail.vue'),
      meta: { requiresAuth: true, roles: FLEET_ROLES, fleetKind: 'trailer' },
    },
    {
      path: '/admin/locations',
      name: 'admin-locations',
      component: () => import('../views/admin/AdminLocations.vue'),
      meta: { requiresAuth: true, roles: ['super_admin', 'admin', 'dispatcher'] }
    },
    {
      // `/new` must precede `/:id` so it isn't captured as an id.
      path: '/admin/locations/new',
      name: 'admin-location-new',
      component: () => import('../views/admin/AdminLocationDetail.vue'),
      meta: { requiresAuth: true, roles: ['super_admin', 'admin', 'dispatcher'] }
    },
    {
      path: '/admin/locations/:id',
      name: 'admin-location-detail',
      component: () => import('../views/admin/AdminLocationDetail.vue'),
      meta: { requiresAuth: true, roles: ['super_admin', 'admin', 'dispatcher'] }
    },
    {
      path: '/admin/clients',
      name: 'admin-clients',
      component: () => import('../views/admin/AdminClients.vue'),
      meta: { requiresAuth: true, roles: ['super_admin', 'admin'] }
    },
    {
      path: '/admin/clients/:id',
      name: 'admin-client-detail',
      component: () => import('../views/admin/AdminClientDetail.vue'),
      meta: { requiresAuth: true, roles: ['super_admin', 'admin'] }
    },
    {
      path: '/admin/invites',
      name: 'admin-invites',
      component: () => import('../views/admin/AdminInvites.vue'),
      meta: { requiresAuth: true, roles: ['super_admin', 'admin'] }
    },
    {
      path: '/admin/stats',
      name: 'admin-stats',
      component: () => import('../views/admin/AdminStats.vue'),
      meta: { requiresAuth: true, roles: ['super_admin', 'admin'] }
    },

    {
      // Staff help and FAQ. Every Afab role that handles invoices or costs can read it.
      path: '/admin/help',
      name: 'admin-help',
      component: () => import('../views/admin/AdminHelp.vue'),
      meta: { requiresAuth: true, roles: [...FLEET_ROLES, 'accounting'] }
    },

    // Accounting routes
    {
      path: '/accounting',
      name: 'accounting-reports',
      component: () => import('../views/accounting/AccountingReports.vue'),
      meta: { requiresAuth: true, roles: ['super_admin', 'admin', 'accounting'] }
    },
    {
      path: '/accounting/fuel',
      name: 'fuel-dashboard',
      component: () => import('../views/accounting/FuelUsageDashboard.vue'),
      meta: { requiresAuth: true, roles: ['super_admin', 'admin', 'accounting'] }
    },

    // Client routes
    {
      path: '/client',
      name: 'client-dashboard',
      component: () => import('../views/client/ClientDashboard.vue'),
      meta: { requiresAuth: true, roles: ['manager', 'viewer'] }
    },
    {
      path: '/client/locations',
      name: 'client-locations',
      component: () => import('../views/client/ClientLocations.vue'),
      meta: { requiresAuth: true, roles: ['manager', 'viewer'] }
    },
    {
      path: '/client/orders/new',
      name: 'client-create-order',
      component: () => import('../views/client/ClientCreateOrder.vue'),
      meta: { requiresAuth: true, roles: ['manager'] }
    },
    {
      path: '/client/orders/empty-trailer',
      name: 'client-empty-trailer-request',
      component: () => import('../views/client/ClientEmptyTrailerRequest.vue'),
      meta: { requiresAuth: true, roles: ['manager'] }
    },
    {
      path: '/client/users',
      name: 'client-users',
      component: () => import('../views/client/ClientUsers.vue'),
      meta: { requiresAuth: true, roles: ['manager'] }
    },

    // Shared routes (all authenticated users)
    {
      path: '/change-password',
      name: 'change-password',
      component: () => import('../views/auth/ChangePasswordView.vue'),
      meta: { requiresAuth: true }
    },

    // Driver routes
    {
      path: '/driver',
      name: 'driver-dashboard',
      component: () => import('../views/driver/DriverDashboard.vue'),
      meta: { requiresAuth: true, roles: ['driver'] }
    },
    {
      // Qualified-driver-only; the component also redirects if not qualified.
      path: '/driver/long-range',
      name: 'driver-long-range',
      component: () => import('../views/driver/DriverLongRange.vue'),
      meta: { requiresAuth: true, roles: ['driver'] }
    },

    {
      // Read-only full view of a freight location. Reachable by drivers from a leg
      // on the long-range board; editing lives on /admin/locations/:id. Open to every
      // role with RLS read access to freight_locations.
      path: '/locations/:id',
      name: 'location-view',
      component: () => import('../views/LocationView.vue'),
      meta: { requiresAuth: true, roles: ['super_admin', 'admin', 'dispatcher', 'driver', 'accounting'] }
    },
  ]
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  // Wait for auth to initialize
  if (auth.loading) {
    await new Promise<void>((resolve) => {
      const stop = watch(() => auth.loading, (loading) => {
        if (!loading) {
          stop()
          resolve()
        }
      }, { immediate: true })
    })
  }

  // Redirect unauthenticated users to login (except invite page)
  if (to.meta.requiresAuth && !auth.isLoggedIn) {
    return '/login'
  }

  // Redirect to change password after password recovery
  if (auth.passwordRecovery && to.name !== 'change-password') {
    return '/change-password'
  }

  // Redirect authenticated users away from guest pages
  if (to.meta.guest && auth.isLoggedIn) {
    return auth.homeRoute
  }

  // Fuel dashboard is feature-flagged off (e.g. in production) — block direct
  // navigation even though the nav link is hidden.
  if (to.name === 'fuel-dashboard' && !FUEL_DASHBOARD_ENABLED) {
    return auth.homeRoute
  }

  // Role-based access check.
  //
  // Against EVERY role the person holds, not just the primary one (HOP-167). Comparing
  // auth.role alone sent the dispatcher who drives away from /driver — he holds the role,
  // but dispatcher outranks it and so was the only thing the guard saw.
  if (to.meta.roles && auth.roles.length && !auth.hasAnyRole(...(to.meta.roles as string[]))) {
    return auth.homeRoute
  }
})

export default router
